import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  CalendarDateRange,
  CalendarDateRangeBounds,
  CalendarDateRangePreset,
  CalendarDateRangeSelection,
} from 'src/modules/shared/types/date-range.type';
import {
  areCalendarDateRangesEqual,
  getCalendarDateRangeIssue,
} from 'src/modules/shared/utils/date-range.util';

/** Configuration for a controlled, explicitly applied date-range draft. */
export interface UseDateRangeDraftOptions<TId extends string> {
  /** Currently applied concrete dates. */
  value: CalendarDateRange;
  /** Currently applied shortcut, when one produced the value. */
  selectedPresetId?: TId | null;
  /** Inclusive selection bounds. */
  bounds?: CalendarDateRangeBounds;
  /** Called once when a valid changed draft is applied. */
  onApply: (selection: CalendarDateRangeSelection<TId>) => void;
}

/** State and actions for an isolated date-range draft. */
export interface UseDateRangeDraftResult<TId extends string> {
  /** Current uncommitted dates. */
  draftValue: CalendarDateRange;
  /** Shortcut that produced the current draft, if any. */
  draftPresetId: TId | null;
  /** First validation issue in the current draft. */
  issue: ReturnType<typeof getCalendarDateRangeIssue>;
  /** Whether the draft differs from the applied value. */
  isDirty: boolean;
  /** Whether the draft is complete, valid, and changed. */
  canApply: boolean;
  /** Replaces the draft with manually selected dates. */
  setDraftValue: (value: CalendarDateRange) => void;
  /** Replaces the draft with a preset value. */
  selectPreset: (preset: CalendarDateRangePreset<TId>) => void;
  /** Restores the draft to the applied value. */
  reset: () => void;
  /** Applies the draft when it is valid and changed. */
  apply: () => boolean;
}

/**
 * Manages draft-versus-applied semantics for any calendar date-range control.
 */
export const useDateRangeDraft = <TId extends string>({
  value,
  selectedPresetId = null,
  bounds = {},
  onApply,
}: UseDateRangeDraftOptions<TId>): UseDateRangeDraftResult<TId> => {
  const [draftValue, setDraftValueState] = useState<CalendarDateRange>(value);
  const [draftPresetId, setDraftPresetId] = useState<TId | null>(
    selectedPresetId
  );
  const appliedFrom = value.from;
  const appliedTo = value.to;

  const reset = useCallback(() => {
    setDraftValueState({ from: appliedFrom, to: appliedTo });
    setDraftPresetId(selectedPresetId);
  }, [appliedFrom, appliedTo, selectedPresetId]);

  useEffect(() => {
    reset();
  }, [reset]);

  const issue = useMemo(
    () => getCalendarDateRangeIssue(draftValue, bounds),
    [bounds, draftValue]
  );
  const isDirty =
    !areCalendarDateRangesEqual(draftValue, value) ||
    draftPresetId !== selectedPresetId;
  const canApply = isDirty && issue === null;

  const setDraftValue = useCallback((nextValue: CalendarDateRange) => {
    setDraftValueState(nextValue);
    setDraftPresetId(null);
  }, []);

  const selectPreset = useCallback((preset: CalendarDateRangePreset<TId>) => {
    setDraftValueState(preset.value);
    setDraftPresetId(preset.id);
  }, []);

  const apply = useCallback((): boolean => {
    if (!canApply || !draftValue.from || !draftValue.to) {
      return false;
    }

    onApply({
      value: { from: draftValue.from, to: draftValue.to },
      presetId: draftPresetId,
    });
    return true;
  }, [canApply, draftPresetId, draftValue.from, draftValue.to, onApply]);

  return {
    draftValue,
    draftPresetId,
    issue,
    isDirty,
    canApply,
    setDraftValue,
    selectPreset,
    reset,
    apply,
  };
};
